import { type TaskDetailsTabType } from '@/components/Modals/TaskDetails/TaskDetails.tsx';
import { type TypedTaskData } from '@/components/Modals/TaskDetails/TaskDetailsTabs/ExecutionPanel.tsx';
import { TaskTime } from '@/components/Modals/TaskDetails/TaskDetailsTabs/TaskTime.tsx';
import { TaskAssigneeAvatars } from '@/components/Studies/Tasks/TaskAssigneeAvatars.tsx';
import Link from '@/components/UI/Link';
import { _isEmptyString, _isNotEmpty, _notNil } from '@/littledash';
import type { PickRequired } from '@/model/Common.model';
import { type GlossaryItem } from '@/model/Glosary.model.ts';
import { type PresetCalculation } from '@/model/PresetCalculation.model.ts';
import { type TaskDuration, type TaskRescheduleV1, type TaskType, type TaskV1 } from '@/model/Task.model';
import { summarizeAssignmentReasons } from '@/model/Task.utils';
import { type TreatmentV1 } from '@/model/Treatment.model.ts';
import { web } from '@/support/route';
import { useModalAction } from '@/utils/modal';
import { type FC, type PropsWithChildren, useEffect, useMemo, useState, useCallback } from 'react';
import { FiUserPlus } from 'react-icons/fi';
import { RiCalendarLine, RiCheckboxCircleFill, RiCheckboxMultipleLine, RiQuestionLine } from 'react-icons/ri';
import { TbCancel } from 'react-icons/tb';
import Button from '@/components/UI/Button';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import styles from '@/components/Modals/AddTask/AddTask.module.scss';
import { DateRenderFormat, DateTimeRenderFormat, DateUtils } from '@/utils/Date.utils.ts';
import { oneOffDateValidator } from '@/components/Modals/AddTask/Steps/AddTaskScheduleForm/AddTaskScheduleForm.utils.ts';
import DatePickerNative from '@/components/UI/DatePickerNative';
import { DurationPicker } from '@/components/UI/DurationPicker/DurationPicker.tsx';
import { differenceInMinutes } from 'date-fns';
import { TimezoneSelect } from '@/components/TimezoneSelect/TimezoneSelect.tsx';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { TaskStatus } from '@/components/Tasks/Show/TaskStatus.ts';

interface TaskInfoRowProps {
  label: string;
}

const taskTypeLabels: Record<TaskType, string> = {
  dosing: 'Dosing',
  measurement: 'Measurement',
  observation: 'Observation',
  sample: 'Sample',
  other: 'Other',
};

const TypedTaskInfoRow: FC<{ task: TaskV1; typedTaskData?: TypedTaskData; onClick: () => void }> = ({
  task,
  typedTaskData,
  onClick,
}) => {
  const { title, items } = useMemo(() => {
    switch (task?.type) {
      case 'measurement': {
        const data: Map<PresetCalculation['id'], PresetCalculation> =
          typedTaskData?.type === 'measurement' ? typedTaskData.data : new Map();
        const items = (task?.execution?.measurement?.measurements ?? []).reduce<Array<string>>((acc, id) => {
          const calculation = data.get(id);
          if (_notNil(calculation)) {
            const unit = _isEmptyString(calculation.unit ?? '') ? '' : ` ( ${calculation.unit} )`;
            return [...acc, `${calculation.name}${unit}`];
          }
          return acc;
        }, []);
        return { title: 'Measurements', items };
      }
      case 'sample': {
        const data: Map<GlossaryItem['id'], GlossaryItem> =
          typedTaskData?.type === 'sample' ? typedTaskData.data : new Map();
        const items = (task?.execution?.sample?.samples ?? []).reduce<Array<string>>((acc, id) => {
          const glossaryItem = data.get(id);
          if (_notNil(glossaryItem)) {
            return [...acc, glossaryItem.title];
          }
          return acc;
        }, []);
        return { title: 'Samples', items };
      }
      case 'observation': {
        const data: Map<GlossaryItem['id'], GlossaryItem> =
          typedTaskData?.type === 'observation' ? typedTaskData.data : new Map();
        const items = (task?.execution?.observation?.observations ?? []).reduce<Array<string>>((acc, id) => {
          const glossaryItem = data.get(id);
          if (_notNil(glossaryItem)) {
            return [...acc, glossaryItem.title];
          }
          return acc;
        }, []);
        return { title: 'Observations', items };
      }
      case 'dosing': {
        const data: Map<TreatmentV1['api_id'], TreatmentV1> =
          typedTaskData?.type === 'dosing' ? typedTaskData.data : new Map();
        const items = (task?.execution?.dosing?.treatments ?? []).reduce<Array<string>>((acc, id) => {
          const treatment = data.get(id);
          if (_notNil(treatment)) {
            return [...acc, treatment.treatment_label];
          }
          return acc;
        }, []);
        return { title: 'Treatments', items };
      }
      default:
        return { title: '', items: [] };
    }
  }, [task, typedTaskData]);
  if (items.length <= 0) {
    return null;
  }
  return (
    <TaskInfoRow label={title}>
      <div className="flex flex-column link" onClick={onClick}>
        {items.slice(0, 3).map((item, idx) => (
          <span key={idx}>{item}</span>
        ))}
        {items.length > 3 ? <span>+{items.length - 3} More</span> : null}
      </div>
    </TaskInfoRow>
  );
};

const TaskInfoRow: FC<PropsWithChildren<TaskInfoRowProps>> = ({ label, children }) => (
  <div className="flex">
    <div className="flex flex-column">
      <p className="ml3" style={{ width: '90px' }}>
        {label}
      </p>
    </div>
    <div className="flex flex-column">
      <p className="black pl3 ui__overflow__scroll_y" style={{ maxWidth: '660px' }}>
        {children}
      </p>
    </div>
  </div>
);

const RescheduleForm: FC<{ task: TaskV1; handleTaskReschedule: () => void; taskRescheduleDisabled: boolean }> = ({
  task,
  handleTaskReschedule,
  taskRescheduleDisabled,
}) => {
  const [rescheduleFormVisible, setRescheduleFormVisible] = useState(false);

  useEffect(() => {
    if (taskRescheduleDisabled) {
      setRescheduleFormVisible(false);
    }
  }, [taskRescheduleDisabled]);

  const { displayTimezoneSelect, timezones } = useMemo(() => {
    const displayTimezoneSelect =
      DateUtils.timezoneOffsetMilliseconds(task.duration.start, task.timezone) !==
      DateUtils.timezoneOffsetMilliseconds(task.duration.start, DateUtils.timezone());
    return {
      displayTimezoneSelect: displayTimezoneSelect,
      timezones: [DateUtils.timezone(), task.timezone],
    };
  }, [task.duration.start, task.timezone]);
  const { invoke: updateTaskTime } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyId}/tasks/{taskId}/reschedule',
    path: { studyId: task.study.api_id, taskId: task.id },
    invokeOnInit: false,
  });

  const initDefaultValues = (taskDuration: TaskDuration): TaskRescheduleV1 & { durationIsValid: boolean } => {
    const startDateTime = DateUtils.roundDate(new Date(taskDuration.start), '30minutes');
    const endDateTime = DateUtils.roundDate(new Date(taskDuration.end), '30minutes');
    const durationInMinutes = differenceInMinutes(endDateTime, startDateTime);
    const durationInHours = durationInMinutes / 60;
    const allDayTask = durationInHours >= 24;
    const isoStartDate = DateUtils.renderDateTime(taskDuration.start, { format: DateRenderFormat.ISODate });

    if (allDayTask) {
      return {
        date: isoStartDate,
        timezone: DateUtils.timezone(),
        time: {
          type: 'all_day',
        },
        durationIsValid: true, // All day tasks are always valid
      };
    }

    // Round the actual datetime objects first, then extract time strings
    const roundedStartTime = DateUtils.roundDate(new Date(taskDuration.start), '30minutes');
    const roundedEndTime = DateUtils.roundDate(new Date(taskDuration.end), '30minutes');

    const isoStartTime = DateUtils.renderTime(
      DateUtils.renderDateTime(roundedStartTime.toISOString(), { format: DateTimeRenderFormat.Time })
    );
    const isoEndTime = DateUtils.renderTime(
      DateUtils.renderDateTime(roundedEndTime.toISOString(), { format: DateTimeRenderFormat.Time })
    );

    // Check if this is a cross-midnight duration by comparing times
    const startMinutes = roundedStartTime.getHours() * 60 + roundedStartTime.getMinutes();
    let endMinutes = roundedEndTime.getHours() * 60 + roundedEndTime.getMinutes();
    // Treat midnight (00:00) as end of day (24:00 = 1440 minutes)
    if (endMinutes === 0) {
      endMinutes = 24 * 60; // 1440 minutes
    }

    const isCrossMidnight = endMinutes < startMinutes;

    return {
      date: isoStartDate,
      timezone: DateUtils.timezone(),
      time: {
        from: isoStartTime,
        to: isoEndTime,
        type: 'timed',
      },
      durationIsValid: !isCrossMidnight, // Mark as invalid if cross-midnight
    };
  };

  const formMethods = useForm<TaskRescheduleV1 & { durationIsValid: boolean }>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: initDefaultValues(task.duration),
  });

  const handleRescheduleTask = async (data: TaskRescheduleV1 & { durationIsValid: boolean }) => {
    // Extract the validation field and send only the actual form data to API
    const { durationIsValid, ...formData } = data;
    const result = await updateTaskTime({
      path: { studyId: task.study.api_id, taskId: task.id },
      body: formData,
    });
    if (result?.type === 'success') {
      handleTaskReschedule();
      setRescheduleFormVisible(false);
    }
  };

  const onDurationValidate = useCallback(
    (isValid: boolean) => {
      // Update the hidden validation field
      formMethods.setValue('durationIsValid', isValid, { shouldValidate: true });

      if (isValid) {
        formMethods.clearErrors('durationIsValid');
      } else {
        formMethods.setError('durationIsValid', {
          type: 'custom',
          message: 'Duration cannot extend past midnight',
        });
      }
    },
    [formMethods]
  );
  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={formMethods.handleSubmit(handleRescheduleTask)}
        className={styles['step-content']}
        data-test-component="RescheduleTaskForm"
        data-test-element="form"
      >
        <div className="mt1 pb2 flex">
          <div className="flex">
            <span className="ml3 mr4 black">
              <RiCalendarLine size={20} />
            </span>
            <div>
              <p className="black fw5 f6">
                <TaskTime timezone={task.timezone} taskDuration={task.duration} />
              </p>

              {!rescheduleFormVisible && !taskRescheduleDisabled && (
                <p
                  data-testid="reschedule-enable"
                  className="black fw4 f6 link"
                  onClick={() => setRescheduleFormVisible(true)}
                >
                  Reschedule
                </p>
              )}
              {rescheduleFormVisible && !taskRescheduleDisabled && (
                <>
                  <div className="pt2">
                    <label className="w4 mb2 lh-title dark-gray">Due Date</label>
                    <Controller
                      name="date"
                      control={formMethods.control}
                      defaultValue={DateUtils.dateNow()}
                      rules={{ validate: (value?: string) => oneOffDateValidator(value) }}
                      render={({ field, fieldState }) => (
                        <>
                          <DatePickerNative
                            value={field.value ?? ''}
                            onChange={(c) => field.onChange(c)}
                            className={styles['add-task-input-field']}
                          />
                          {fieldState.error && <span className="f6 red db pt2">{fieldState.error.message}</span>}
                        </>
                      )}
                    />
                  </div>
                  <div>
                    <label className="w4 mb2 lh-title dark-gray">Duration</label>
                    <Controller
                      name="time"
                      control={formMethods.control}
                      defaultValue={{ type: 'all_day' }}
                      render={({ field }) => (
                        <DurationPicker
                          value={field.value ?? { type: 'all_day' }}
                          disabled={field.disabled}
                          onChange={field.onChange}
                          onValidationChange={onDurationValidate}
                        />
                      )}
                    />
                    {/* Hidden field for validation state */}
                    <Controller
                      name="durationIsValid"
                      control={formMethods.control}
                      defaultValue={true}
                      rules={{
                        validate: (value) => value === true || 'Duration cannot extend past midnight',
                      }}
                      render={() => <></>}
                    />
                    {formMethods.formState.errors.durationIsValid && (
                      <span className="f6 red db pt2">{formMethods.formState.errors.durationIsValid.message}</span>
                    )}
                  </div>
                  {displayTimezoneSelect && (
                    <>
                      <div className="pt3" data-testid="task-reschedule-timezone">
                        <label className="w4 mb2 lh-title dark-gray">Timezone</label>
                        <Controller
                          name="timezone"
                          control={formMethods.control}
                          defaultValue={DateUtils.timezone()}
                          render={({ field }) => (
                            <TimezoneSelect
                              timezones={timezones}
                              value={field.value ?? DateUtils.timezone()}
                              disabled={formMethods.formState.isSubmitting || formMethods.formState.disabled}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                              className="w-90"
                            />
                          )}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          {rescheduleFormVisible && !taskRescheduleDisabled && (
            <div className="ml-auto">
              <Button
                testId="submit-reschedule-button"
                submit
                testKey="RescheduleTaskForm"
                disabled={!formMethods.formState.isValid || formMethods.formState.isSubmitting}
              >
                Save
              </Button>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export const OverviewPanel: FC<{
  task: PickRequired<TaskV1, 'animals'>;
  typedTaskData?: TypedTaskData;
  setActiveTab: (tab: TaskDetailsTabType) => void;
  handleTaskReschedule: () => void;
  taskRescheduleDisabled: boolean;
}> = ({ task, typedTaskData, setActiveTab, handleTaskReschedule, taskRescheduleDisabled }) => {
  const { closeModal } = useModalAction();
  const groupCount = useMemo(
    () =>
      task.animals.reduce((acc, { group }) => {
        if (_notNil(group)) {
          acc.add(group.api_id);
        }
        return acc;
      }, new Set()).size,
    [task]
  );

  const assignmentSummary = useMemo(() => summarizeAssignmentReasons(task.animals), [task.animals]);

  return (
    <div className="pt3 pr4" data-test-component="OverviewPanel" data-test-element="container">
      <div className="pl4">
        <div className="pa3 mt2 w-100 bg-light-gray">
          <RescheduleForm
            task={task}
            handleTaskReschedule={handleTaskReschedule}
            taskRescheduleDisabled={taskRescheduleDisabled}
          />
          <div className="flex mt2">
            <span className="ml3 mr4 black">
              <RiCheckboxMultipleLine size={20} />
            </span>
            <p className="black fw4 f6 link" onClick={() => setActiveTab('target')}>
              Affects {task.animals.length} animals{' '}
              {task.target.type === 'group' && (
                <>
                  in {groupCount} / {task.target.groups.length} groups
                </>
              )}
            </p>
          </div>
          {assignmentSummary !== '' && (
            <div className="flex mt2">
              <span className="ml3 mr4 black" style={{ position: 'relative', display: 'inline-flex' }}>
                <RiCalendarLine size={20} />
                <span
                  style={{
                    position: 'absolute',
                    bottom: -2,
                    right: -4,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 12,
                    height: 12,
                  }}
                >
                  <RiQuestionLine size={10} />
                </span>
              </span>
              <p className="black fw4 f6">{assignmentSummary}</p>
            </div>
          )}
          {task.status === TaskStatus.done && (
            <div className="flex mt2">
              <span className="ml3 mr4 black">
                <RiCheckboxCircleFill className="green" size={20} />
              </span>
              <div>
                <p className="black fw5 f6">Marked as complete {DateUtils.renderDateTime(task.transitioned_at)}</p>
                {_notNil(task.transitioned_by) && <p className="mid-gray fw5 f6">by {task.transitioned_by.name}</p>}
              </div>
            </div>
          )}
          {task.status === TaskStatus.cancelled && (
            <div className="flex mt2">
              <span className="ml3 mr4 black">
                <TbCancel className="gray" size={20} />
              </span>
              <div>
                <p className="black fw5 f6">Marked as cancelled {DateUtils.renderDateTime(task.transitioned_at)}</p>
                {_notNil(task.transitioned_by) && <p className="mid-gray fw5 f6">by {task.transitioned_by.name}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mv4 pl3 ml1 f6 min-h4 w-100">
        <div className="mt3">
          <TaskInfoRow label="Type">{taskTypeLabels[task.type]}</TaskInfoRow>
        </div>
        <TypedTaskInfoRow task={task} typedTaskData={typedTaskData} onClick={() => setActiveTab('execution')} />
        <div className="mt3">
          <TaskInfoRow label="Study">
            <Link className="link" to={web('studies.show', { id: task.study.id })} onClick={() => closeModal()}>
              {task.study.name}
            </Link>
          </TaskInfoRow>
        </div>

        {_isNotEmpty(task.assignees) && (
          <div className="mt2">
            <TaskInfoRow label="Assignees">
              <div className="flex items-center" onClick={() => setActiveTab('assignees')}>
                <TaskAssigneeAvatars assignees={task.assignees} max={5} className="mr1" />
                {task.study.active && (
                  <div className="flex pa2 link" style={{ border: '1px dashed lightgrey', borderRadius: '23px' }}>
                    <FiUserPlus size={20} />
                  </div>
                )}
              </div>
            </TaskInfoRow>
          </div>
        )}
        {_isNotEmpty(task.description) && (
          <div className="mt2">
            <TaskInfoRow label="Description">{task.description}</TaskInfoRow>
          </div>
        )}
      </div>
    </div>
  );
};
